<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-[#22282F]">Directions</h2>
        <button
          v-if="!isDirectionScopedAdmin"
          @click="openCreateModal"
          class="px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors flex items-center gap-2"
        >
          <Plus class="w-4 h-4" />
          Nouvelle Direction
        </button>
      </div>

      <!-- Search Bar -->
      <div class="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher une direction par nom ou code..."
            class="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
          />
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-12 bg-white border border-gray-200 rounded-xl">
        <div class="w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
      </div>

      <!-- Directions Grid -->
      <div v-else-if="filteredDirections.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="direction in filteredDirections"
          :key="direction.id"
          class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          @click="goToDepartements(direction)"
        >
          <div class="flex items-start justify-between mb-4">
            <div class="p-3 bg-gray-50 rounded-lg group-hover:bg-[#22282F] group-hover:text-white transition-colors">
              <Building2 class="w-6 h-6" />
            </div>
            <div class="flex gap-2">
              <button
                @click.stop="openEditModal(direction)"
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Modifier"
              >
                <Edit3 class="w-4 h-4 text-gray-600" />
              </button>
              <button
                v-if="canDeleteDirection(direction)"
                @click.stop="deleteDirection(direction.id)"
                class="p-2 hover:bg-red-50 rounded-lg transition-colors"
                title="Supprimer"
              >
                <Trash2 class="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>

          <h3 class="text-lg font-semibold text-[#22282F] mb-1">{{ direction.nom }}</h3>
          <p class="text-sm text-gray-500 mb-4">{{ direction.code }}</p>

          <div class="flex items-center justify-between pt-4 border-t border-gray-100">
            <div class="flex items-center gap-2 text-sm text-gray-600">
              <Layers class="w-4 h-4" />
              <span>Département(s)</span>
            </div>
            <ChevronRight class="w-5 h-5 text-gray-400 group-hover:text-[#22282F] transition-colors" />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white border border-gray-200 rounded-xl p-12 text-center">
        <Building2 class="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-semibold text-[#22282F] mb-2">Aucune direction trouvée</h3>
        <p class="text-sm text-gray-500">
          {{ searchQuery ? 'Essayez de modifier votre recherche' : 'Créez votre première direction pour commencer' }}
        </p>
      </div>

      <!-- Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
        @click.self="closeModal"
      >
        <div class="bg-white rounded-xl max-w-md w-full p-6">
          <h3 class="text-lg font-bold text-[#22282F] mb-4">
            {{ editingDirection ? 'Modifier la direction' : 'Nouvelle direction' }}
          </h3>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-[#22282F] mb-2">Code</label>
              <input
                v-model="form.code"
                type="text"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                placeholder="ex: DIR-01"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-[#22282F] mb-2">Nom</label>
              <input
                v-model="form.nom"
                type="text"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                placeholder="ex: Direction Générale"
              />
            </div>
          </div>
          <div class="flex gap-3 mt-6">
            <button
              @click="closeModal"
              class="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              @click="saveDirection"
              class="flex-1 px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors"
            >
              {{ editingDirection ? 'Modifier' : 'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Building2, Layers, Plus, Edit3, Trash2, Search, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { useAuthStore } from '@/store/auth'

const toast = useToast()
const authStore = useAuthStore()

interface Direction {
  id: number
  code: string
  nom: string
  departements_count?: number
}

const router = useRouter()
const loading = ref(false)
const directions = ref<Direction[]>([])
const searchQuery = ref('')
const showModal = ref(false)
const editingDirection = ref<Direction | null>(null)
const form = ref({ code: '', nom: '' })

const isDirectionScopedAdmin = computed(() => {
  const roleCode = authStore.user?.role?.code
  return !!authStore.user?.direction && (roleCode === 'ADMIN' || roleCode === 'DIRECTEUR')
})

const canDeleteDirection = (direction: Direction) => {
  const roleCode = authStore.user?.role?.code
  const adminDirectionId = authStore.user?.direction?.id
  if (roleCode === 'DIRECTEUR' && adminDirectionId === direction.id) {
    return false
  }
  return true
}

const filteredDirections = computed(() => {
  if (!searchQuery.value) return directions.value
  const query = searchQuery.value.toLowerCase()
  return directions.value.filter(d =>
    d.nom.toLowerCase().includes(query) || d.code.toLowerCase().includes(query)
  )
})

const fetchDirections = async () => {
  loading.value = true
  try {
    directions.value = await apiFetch('/api/admin/directions/')
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger les directions')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingDirection.value = null
  form.value = { code: '', nom: '' }
  showModal.value = true
}

const openEditModal = (direction: Direction) => {
  editingDirection.value = direction
  form.value = { code: direction.code, nom: direction.nom }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingDirection.value = null
}

const saveDirection = async () => {
  try {
    const url = editingDirection.value
      ? `/api/admin/directions/${editingDirection.value.id}/update/`
      : '/api/admin/directions/create/'

    await apiFetch(url, {
      method: editingDirection.value ? 'PUT' : 'POST',
      body: JSON.stringify(form.value)
    })

    toast.success(
      editingDirection.value ? 'Direction modifiée' : 'Direction créée',
      'L\'opération a réussi'
    )
    await fetchDirections()
    closeModal()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de sauvegarder la direction')
  }
}

const deleteDirection = async (id: number) => {
  const target = directions.value.find(direction => direction.id === id)
  if (target && !canDeleteDirection(target)) {
    toast.error('Action non autorisée', 'Un directeur ne peut pas supprimer sa direction')
    return
  }

  try {
    await apiFetch(`/api/admin/directions/${id}/delete/`, {
      method: 'DELETE'
    })

    toast.success('Direction supprimée', 'La direction a été supprimée avec succès')
    await fetchDirections()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de supprimer la direction')
  }
}

const goToDepartements = (direction: Direction) => {
  router.push(`/organisation/directions/${direction.id}/departements`)
}

onMounted(() => {
  fetchDirections()
})
</script>
